import { yupResolver } from '@hookform/resolvers/yup';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { filter, forEach, get, includes, isEmpty, isEqual, map, size, some } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { runMultiplexThreshold } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { getModelsTypeByModelInferences } from 'interfaces/calculateFeatures';
import { BinaryClassifierConfig } from 'interfaces/inferenceArtifacts';
import { MultiplexThresholdConfig } from 'interfaces/jobs/multiplex/thresholdParams';
import { Permission } from 'interfaces/permissionOption';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { humanize } from 'utils/helpers';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { useCasesParams } from 'utils/useCasesParams';
import { usePermissions } from 'utils/usePermissions';
import { ActionStepper } from '../ActionStepper';
import InferenceModelsForSlides from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { useArtifactResultsQuery } from '../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import { JobNameAndDescriptionStep } from '../utils';
import { BinaryClassifierThresholdFlowParameters } from './BinaryClassifierThresholdFlowParameters';
import { MultiplexSelectionAlert } from './MultiplexSelectionAlert';
import { useMultiplexActionSelection } from './useMultiplexActionSelection';

export { SliderInputWithLabel } from './BinaryClassifierThresholdFlowParameters';
export type { SliderInputWithLabelProps } from './BinaryClassifierThresholdFlowParameters';

const SNACK_BAR_KEY_RUN_MULTIPLEX_THRESHOLD = 'RUN_MULTIPLEX_NORMALIZATION';

export const defaultThresholdConfig: MultiplexThresholdConfig = {
  binaryClassifierInferenceArtifactUrls: [],
  rangeStart: 0.01,
  rangeEnd: 0.95,
  maxThreshold: 0.5,
  precisionDecimal: 3,
};

export interface ThresholdFormValues {
  jobName: string;
  jobDescription: string;
  configParams: MultiplexThresholdConfig;
}

const validationSchema = [
  yup.object({
    configParams: yup.object({
      binaryClassifierInferenceArtifactUrls: yup
        .array()
        .required('Binary classifier results are required')
        .min(1, 'Binary classifier results are required'),
    }),
  }),
  yup.object({}),
];

export interface RunMultiplexThresholdProps {
  onClose: (event?: any, force?: boolean) => void;
}

export const RunMultiplexThreshold = (props: RunMultiplexThresholdProps): ReactElement => {
  const { onClose } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const currentValidationSchema = validationSchema[activeStep];

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const { casesParams } = useCasesParams();
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;

  const studyId = multiplexCasesParams.filters?.studyId;
  const enableInferenceArtifactsQuery = Boolean(studyId);

  const {
    data: inferenceArtifacts,
    isLoading: isInferenceArtifactsLoading,
    isFetching: isInferenceArtifactsFetching,
  } = useArtifactResultsQuery({
    caseParams: multiplexCasesParams,
    config: BinaryClassifierConfig,
    enabled: enableInferenceArtifactsQuery && Boolean(effectiveCasesParams),
  });

  const { data: slides, isLoading: isSlidesLoading } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', multiplexCasesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(multiplexCasesParams, signal),
    enabled: Boolean(studyId) && Boolean(effectiveCasesParams),
  });

  const orchestrationSelectionsState = useOrchestrationSelections();
  const { selectedOrchestrations } = orchestrationSelectionsState;

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors, isDirty },
  } = useForm<ThresholdFormValues>({
    mode: 'onChange',
    defaultValues: {
      configParams: defaultThresholdConfig,
    },
    resolver: yupResolver(currentValidationSchema),
  });
  useTrackActionFormDirty(isDirty);

  useEffect(() => {
    const finalBinaryClassifierArtifactUrls: string[] = [];

    const slidesWithResults = filter(slides, (slide) => {
      return !isEmpty(selectedOrchestrations[slide.slideId]);
    });

    forEach(slidesWithResults, (slide) => {
      forEach(selectedOrchestrations[slide.slideId], (orchestrationData) => {
        finalBinaryClassifierArtifactUrls.push(
          orchestrationData?.orchestration.orchestrationResultArtifactUrlPattern.replace('{slide_id}', slide.slideId)
        );
      });
    });

    const currentBinaryClassifierArtifactUrls = getValues('configParams.binaryClassifierInferenceArtifactUrls');

    if (isEqual(currentBinaryClassifierArtifactUrls, finalBinaryClassifierArtifactUrls)) {
      return;
    }

    setValue('configParams.binaryClassifierInferenceArtifactUrls', finalBinaryClassifierArtifactUrls);
    trigger('configParams.binaryClassifierInferenceArtifactUrls');
  }, [getValues, selectedOrchestrations, setValue, trigger, slides]);

  const checkValidationAndSetIsStepFailed = (stepIndex: number, objectToValidate: Record<string, any>) => {
    validationSchema[stepIndex]
      .validate(objectToValidate)
      .then(() => {
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: false,
        }));
      })
      .catch(() => {
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: true,
        }));
      });
  };

  const runMultiplexThresholdMutation = useMutation(runMultiplexThreshold, {
    onError: () => {
      enqueueSnackbar('Error occurred, Multiplex Threshold failed', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Multiplex Threshold Started', { variant: 'success' });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_THRESHOLD);
    },
  });

  const [doesAllSlidesHaveBinaryArtifacts, setDoesAllSlidesHaveBinaryArtifacts] = useState(true);

  const validateSlidesResultsCount = () => {
    const multiplexSlideIds = map(filter(slides, { stainingType: MULTIPLEX_STAIN_ID }), 'slideId');
    const doesSlideIdHaveBinaryArtifact = (slideId: string) => {
      return some(watch('configParams').binaryClassifierInferenceArtifactUrls, (binaryArtifactUrl) =>
        includes(binaryArtifactUrl, slideId)
      );
    };

    const multiplexSlideIdsWithoutResult =
      filter(multiplexSlideIds, (slideId) => !doesSlideIdHaveBinaryArtifact(slideId)) ?? [];

    if (size(multiplexSlideIdsWithoutResult) > 0) {
      console.warn('slide ids without binary classifier results', multiplexSlideIdsWithoutResult);
      setDoesAllSlidesHaveBinaryArtifacts(false);
      return false;
    } else {
      setDoesAllSlidesHaveBinaryArtifacts(true);
      return true;
    }
  };

  const onSubmit: SubmitHandler<ThresholdFormValues> = async (data) => {
    if (!validateSlidesResultsCount()) {
      setIsStepFailed((prev) => ({
        ...prev,
        [0]: true,
      }));
      return;
    }

    runMultiplexThresholdMutation.mutate({
      ...multiplexCasesParams,
      ...data,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Multiplex Threshold to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_THRESHOLD,
      autoHideDuration: null,
    });

    onClose(undefined, true);
  };

  const validateThresholdParams = async () => {
    checkValidationAndSetIsStepFailed(0, {
      configParams: watch('configParams'),
    });
    // we are checking this in a different flow because we dont have the slides count in the form
    // so the validation schema is not enough
    if (!validateSlidesResultsCount()) {
      setIsStepFailed((prev) => ({
        ...prev,
        [0]: true,
      }));
    }
  };

  const steps = [
    {
      label: 'Threshold Configuration',
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {get(errors, 'configParams.binaryClassifierInferenceArtifactUrls') && (
              <Typography color="error">
                {humanize(get(errors, 'configParams.binaryClassifierInferenceArtifactUrls')?.message)}
              </Typography>
            )}
            {!doesAllSlidesHaveBinaryArtifacts && (
              <Typography color="error">{`There are multiplex slides without binary classifier results. See 'Stain Summary'`}</Typography>
            )}
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slides}
              modelsType={getModelsTypeByModelInferences(inferenceArtifacts)}
              inferenceModels={inferenceArtifacts}
              isLoading={isInferenceArtifactsLoading || isInferenceArtifactsFetching || isSlidesLoading}
              {...orchestrationSelectionsState}
            />
          </Grid>
          <BinaryClassifierThresholdFlowParameters control={control} />
        </Grid>
      ),
      onNextOrBackClick: validateThresholdParams,
    },
    {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
  ];

  const { hasPermission } = usePermissions();
  const canViewJsonJobParams = hasPermission(Permission.ViewJsonJobParams);

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <Grid container direction="column" spacing={2}>
      {multiplexSelectionInfoMessage ? (
        <Grid item>
          <MultiplexSelectionAlert
            severity="info"
            title="This action runs on slides"
            message={multiplexSelectionInfoMessage}
          />
        </Grid>
      ) : null}
      <Grid item>
        <ActionStepper
          handleSubmit={handleSubmit(onSubmit)}
          steps={steps}
          setActiveStepForValidation={setActiveStep}
          isStepFailed={isStepFailed}
        />
      </Grid>
      {canViewJsonJobParams && (
        <Grid item>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandIcon />}>Threshold Params Summary (JSON)</AccordionSummary>
            <AccordionDetails>
              <Typography component="pre">
                {JSON.stringify(
                  {
                    jobName: watch('jobName'),
                    jobDescription: watch('jobDescription'),
                    configParams: watch('configParams'),
                  },
                  null,
                  2
                )}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </Grid>
  );
};
